// HELPER-KPIS (design docs/design/2026-11-02-board-stages-gates-kpis.md §4): the helper metric
// catalog as data, like `de-metric-catalog.ts` (zod-free, type imports only, so the web bundle can
// read it under its CSP).
//
// Helpers (`workforce_agent`) have no seat, key or run: the DE acts for them and names the helper
// on its work tools (`helper`), so the event carries `actor_helper_id`. The platform counts from
// those events: the `proj.helper_kpi` consumer (services/api/src/kpi/helper-project.ts) writes
// weekly `observation` rows with `subject_kind: "agent"` (`subject_id = workforce_agent.id`), and
// two DE seat metrics on the seat that leads the board. A helper self-reports only what no event
// shows (`outcomes_record {helper}`, the `manual` entries below).
//
// Column names are configuration, never code: a metric counts moves between column ROLES, and a
// board maps each role to its status keys (`board.settings.helper_kpi_columns`). Absent, a role
// is the status of the same key (the content-pipeline template's keys), so a board without those
// columns simply produces no helper numbers.
import type { EventQuery } from "./contracts/rows";

/** Off unless `COS_HELPER_KPIS=1` (attribution, the projection and the read route). */
export const HELPER_KPIS_FLAG = "COS_HELPER_KPIS";

/** The column roles a helper metric counts moves between. */
export const HELPER_KPI_ROLES = [
  "research",
  "concepts",
  "ideas",
  "in_work",
  "review",
  "rework",
  "approved",
  "rejected",
] as const;
export type HelperKpiRole = (typeof HELPER_KPI_ROLES)[number];

/** Role → the board's status keys that play it (`board.settings.helper_kpi_columns`). */
export type HelperKpiColumns = Partial<Record<HelperKpiRole, string[]>>;

/** A role with no mapping is the status of the same key. */
export const DEFAULT_HELPER_KPI_COLUMNS: Record<HelperKpiRole, string[]> = Object.fromEntries(
  HELPER_KPI_ROLES.map((r) => [r, [r]]),
) as Record<HelperKpiRole, string[]>;

/** A critic's verdict: a field set on the card (by name, case-insensitive) with a PASS value. */
export type HelperVerdictConfig = { field_name: string; pass_values: string[] };
export const DEFAULT_HELPER_VERDICT: HelperVerdictConfig = {
  field_name: "Verdict",
  pass_values: ["PASS"],
};

/** Who a metric is about: a helper, or the seat that leads the board (the DE). */
export type HelperMetricSubject = "agent" | "seat";

/** Which helpers a metric reads naturally for (a display hint; every helper gets every metric). */
export type HelperMetricFor = "researcher" | "maker" | "critic" | "any" | "de";

export type HelperMetricDef = {
  key: string;
  name: string;
  description: string;
  unit: "number" | "percent" | "time";
  direction: "gte" | "lte";
  aggregation: "cumulative" | "average";
  cadence: "weekly";
  /** `event`: derived by `proj.helper_kpi`; `manual`: self-reported (`outcomes_record {helper}`). */
  source: "event" | "manual";
  /** The events it is read from (documentation and the `metric_def` shape check; not a query). */
  event_query: EventQuery | null;
  subject: HelperMetricSubject;
  for: HelperMetricFor;
};

const fromEvents = (event_type: string) => ({ event_type, measure: "count" }) as EventQuery;
const moves = fromEvents("task.status_changed");
const base = { cadence: "weekly", aggregation: "cumulative", direction: "gte" } as const;

export const HELPER_METRIC_CATALOG = [
  // Researcher: on the cards it filed (the helper named on `task.created`).
  {
    ...base,
    key: "hp.findings_filed",
    name: "Findings filed",
    description: "Cards it filed into Research this week.",
    unit: "number",
    source: "event",
    event_query: fromEvents("task.created"),
    subject: "agent",
    for: "researcher",
  },
  {
    ...base,
    key: "hp.screened_kept",
    name: "Screened kept",
    description: "Its cards moved Research → Concepts this week.",
    unit: "number",
    source: "event",
    event_query: moves,
    subject: "agent",
    for: "researcher",
  },
  {
    ...base,
    key: "hp.screened_dropped",
    name: "Screened dropped",
    description: "Its cards moved Research → Rejected this week.",
    unit: "number",
    source: "event",
    event_query: moves,
    subject: "agent",
    for: "researcher",
  },
  {
    ...base,
    key: "hp.reached_ideas",
    name: "Reached Ideas",
    description: "Its cards that entered Ideas for the first time this week.",
    unit: "number",
    source: "event",
    event_query: moves,
    subject: "agent",
    for: "researcher",
  },
  {
    ...base,
    key: "hp.yield",
    name: "Yield",
    description:
      "Of the cards it filed this week, the share that has reached Approved since (recounted as they move).",
    unit: "percent",
    aggregation: "average",
    source: "event",
    event_query: moves,
    subject: "agent",
    for: "researcher",
  },
  {
    ...base,
    key: "hp.screen_time_median",
    name: "Research → Concepts (median)",
    description: "Median time from filing to Concepts, for its cards screened in this week.",
    unit: "time",
    aggregation: "average",
    direction: "lte",
    source: "event",
    event_query: moves,
    subject: "agent",
    for: "researcher",
  },
  // Makers (copywriters, designers): on the cards it worked while In work or Rework.
  {
    ...base,
    key: "hp.delivered",
    name: "Delivered",
    description: "Cards it worked that moved In work → Review this week.",
    unit: "number",
    source: "event",
    event_query: moves,
    subject: "agent",
    for: "maker",
  },
  {
    ...base,
    key: "hp.approved",
    name: "Approved",
    description: "Cards it worked that moved Review → Approved this week.",
    unit: "number",
    source: "event",
    event_query: moves,
    subject: "agent",
    for: "maker",
  },
  {
    ...base,
    key: "hp.reworked",
    name: "Sent back",
    description: "Cards it worked that were sent to Rework this week.",
    unit: "number",
    direction: "lte",
    source: "event",
    event_query: moves,
    subject: "agent",
    for: "maker",
  },
  {
    ...base,
    key: "hp.rejected",
    name: "Rejected",
    description: "Cards it worked that were moved to Rejected this week.",
    unit: "number",
    direction: "lte",
    source: "event",
    event_query: moves,
    subject: "agent",
    for: "maker",
  },
  {
    ...base,
    key: "hp.first_time_approval",
    name: "First-time approval",
    description: "Of this week's approvals of cards it worked, the share never sent to Rework.",
    unit: "percent",
    aggregation: "average",
    source: "event",
    event_query: moves,
    subject: "agent",
    for: "maker",
  },
  {
    ...base,
    key: "hp.rework_rounds",
    name: "Rework rounds per card",
    description: "Average times a card it worked went to Rework, for cards approved this week.",
    unit: "number",
    aggregation: "average",
    direction: "lte",
    source: "event",
    event_query: moves,
    subject: "agent",
    for: "maker",
  },
  // Critics: verdicts set on cards (a field, by name).
  {
    ...base,
    key: "hp.verdicts",
    name: "Verdicts issued",
    description: "Verdicts it set on cards this week.",
    unit: "number",
    source: "event",
    event_query: fromEvents("task.field_changed"),
    subject: "agent",
    for: "critic",
  },
  {
    ...base,
    key: "hp.pass_reworked",
    name: "Passes later reworked",
    description:
      "Of its PASS verdicts this week, the share whose card a person later sent to Rework (lower is better).",
    unit: "percent",
    aggregation: "average",
    direction: "lte",
    source: "event",
    event_query: fromEvents("task.field_changed"),
    subject: "agent",
    for: "critic",
  },
  // Every helper.
  {
    ...base,
    key: "hp.cards_completed",
    name: "Cards completed",
    description: "Cards it worked on that were completed this week.",
    unit: "number",
    source: "event",
    event_query: moves,
    subject: "agent",
    for: "any",
  },
  {
    ...base,
    key: "hp.cycle_time_median",
    name: "Cycle time (median)",
    description: "Median time from its first work on a card to the card's completion.",
    unit: "time",
    aggregation: "average",
    direction: "lte",
    source: "event",
    event_query: moves,
    subject: "agent",
    for: "any",
  },
  // Self-reported at task end (no event shows these).
  {
    ...base,
    key: "hp.sources_checked",
    name: "Sources checked",
    description: "Sources it read this week (self-reported, with the list as evidence).",
    unit: "number",
    source: "manual",
    event_query: null,
    subject: "agent",
    for: "researcher",
  },
  {
    ...base,
    key: "hp.sources_discovered",
    name: "New sources",
    description: "Sources it found for the first time this week (self-reported).",
    unit: "number",
    source: "manual",
    event_query: null,
    subject: "agent",
    for: "researcher",
  },
  {
    ...base,
    key: "hp.redo_count",
    name: "Redos per set",
    description: "Times it redid a set before handing it over (self-reported).",
    unit: "number",
    direction: "lte",
    source: "manual",
    event_query: null,
    subject: "agent",
    for: "maker",
  },
  // The DE that leads the board (its seat): the pipeline as a whole.
  {
    ...base,
    key: "de.pipeline_yield",
    name: "Pipeline yield",
    description:
      "Of the cards that entered Concepts this week on boards it leads, the share that has reached Approved since.",
    unit: "percent",
    aggregation: "average",
    source: "event",
    event_query: moves,
    subject: "seat",
    for: "de",
  },
  {
    ...base,
    key: "de.first_time_approval_board",
    name: "First-time approval (boards)",
    description:
      "Of this week's approvals on boards it leads, the share never sent to Rework (from the board's moves).",
    unit: "percent",
    aggregation: "average",
    source: "event",
    event_query: moves,
    subject: "seat",
    for: "de",
  },
] as const satisfies readonly HelperMetricDef[];

export type HelperMetricKey = (typeof HELPER_METRIC_CATALOG)[number]["key"];

/** Every key the helper projection owns (`proj.kpi` leaves their cells alone). */
export const HELPER_METRIC_KEYS: ReadonlySet<string> = new Set(
  HELPER_METRIC_CATALOG.map((m) => m.key),
);

/** The keys a helper may self-report through `outcomes_record {helper}` (kind `value`). */
export const HELPER_SELF_REPORTED_KEYS: ReadonlySet<string> = new Set(
  HELPER_METRIC_CATALOG.filter((m) => m.source === "manual").map((m) => m.key),
);
